/**
 * Find Your Mysa - Interactive hover animation component
 *
 * Features:
 * - Images pop up from bottom as cursor moves horizontally across block
 * - Smooth GSAP transitions with scale, rotation, and opacity
 * - Dynamic sizing based on container width
 * - Responsive recalculation on resize
 */

class FindYourMysa extends HTMLElement {
  /** @type {HTMLElement[]} */
  images = [];

  /** @type {number} */
  containerWidth = 0;

  /** @type {DOMRect | null} */
  containerRect = null;

  // Configuration
  config = {
    aspectRatio: 5 / 4,
    paddingX: 32,
    bottomPadding: 32,
    hideBuffer: 5,
    proximityRange: 0.25,
    maxScale: 1.1,
    maxRotationDeg: 3,
    maxHeightConstraint: 0.75,
  };

  // Calculated values
  imageWidth = 0;
  imageHeight = 0;
  fullPopupY = 0;
  startY = 0;

  // Bound event handlers as arrow functions
  #handleMouseEnter = () => {
    this.#calculateAndApplySizes();
  };

  /** @param {MouseEvent} event */
  #handleMouseMove = (event) => {
    if (this.containerWidth === 0 || !this.containerRect) {
      this.#calculateAndApplySizes();
      if (this.containerWidth === 0) return;
    }

    const mouseX = event.clientX - this.containerRect.left;
    const percentX = gsap.utils.clamp(0, 1, mouseX / this.containerWidth);

    const numImages = this.images.length;
    const { proximityRange, maxScale } = this.config;

    for (const [index, img] of this.images.entries()) {
      const targetP = (index + 1) / (numImages + 1);
      const startP = targetP - proximityRange;
      const endP = targetP + proximityRange;

      let popUpValue;

      if (percentX < targetP) {
        popUpValue = gsap.utils.mapRange(startP, targetP, 0, 1, percentX);
      } else {
        popUpValue = gsap.utils.mapRange(targetP, endP, 1, 0, percentX);
      }

      popUpValue = gsap.utils.clamp(0, 1, popUpValue);

      // Calculate animation properties
      const rangeY = this.fullPopupY - this.startY;
      const newY = this.startY + popUpValue * rangeY;
      const newOpacity = popUpValue > 0 ? 1.0 : 0;
      const newScale = popUpValue * (maxScale - 1.0) + 1.0;

      const targetRotation = parseFloat(img.dataset.initialRotation || "0");
      const newRotation = popUpValue * targetRotation;

      gsap.to(img, {
        y: newY,
        opacity: newOpacity,
        scale: newScale,
        rotation: newRotation,
        duration: 0.2,
      });
    }
  };

  #handleMouseLeave = () => {
    gsap.to(this.images, {
      y: this.startY,
      opacity: 0,
      scale: 1.0,
      rotation: 0,
      duration: 0.5,
      ease: "power2.out",
    });
  };

  connectedCallback() {
    this.images = Array.from(this.querySelectorAll("[data-hover-image]"));

    if (this.images.length === 0) return;

    this.#calculateAndApplySizes();
    this.#setupInitialPositions();
    this.#attachEventListeners();
  }

  disconnectedCallback() {
    this.#removeEventListeners();
  }

  #attachEventListeners() {
    this.addEventListener("mouseenter", this.#handleMouseEnter);
    this.addEventListener("mousemove", this.#handleMouseMove);
    this.addEventListener("mouseleave", this.#handleMouseLeave);
  }

  #removeEventListeners() {
    this.removeEventListener("mouseenter", this.#handleMouseEnter);
    this.removeEventListener("mousemove", this.#handleMouseMove);
    this.removeEventListener("mouseleave", this.#handleMouseLeave);
  }

  #calculateAndApplySizes() {
    this.containerWidth = this.offsetWidth;
    this.containerRect = this.getBoundingClientRect();
    const containerHeight = this.offsetHeight;
    const numImages = this.images.length;

    const {
      paddingX,
      bottomPadding,
      hideBuffer,
      aspectRatio,
      maxHeightConstraint,
    } = this.config;

    // Calculate available width and dynamic image size
    const totalAvailableWidth = this.containerWidth - 2 * paddingX;
    const totalGapSpace = numImages > 1 ? (numImages - 1) * bottomPadding : 0;
    this.imageWidth = (totalAvailableWidth - totalGapSpace) / numImages;
    this.imageHeight = this.imageWidth * aspectRatio;

    // Define Y start and desired Y end
    this.startY = this.imageHeight + hideBuffer;
    const desiredGapY = -(this.imageHeight + bottomPadding);

    // Define Y end based on height constraint
    const maxYPositionFromBottom =
      containerHeight * maxHeightConstraint - this.imageHeight;
    const maxYTranslationCap = -maxYPositionFromBottom;

    // Set final pop-up position
    this.fullPopupY = Math.max(desiredGapY, maxYTranslationCap);

    // Apply dimensions and initial position
    for (const img of this.images) {
      gsap.set(img, {
        width: this.imageWidth,
        height: this.imageHeight,
        y: this.startY,
      });
    }
  }

  #setupInitialPositions() {
    const numImages = this.images.length;
    const { maxRotationDeg } = this.config;

    for (const [index, img] of this.images.entries()) {
      const targetPercentage = (index + 1) / (numImages + 1);

      gsap.set(img, {
        left: `${targetPercentage * 100}%`,
        xPercent: -50,
        transformOrigin: "50% 100%",
      });

      // Assign random rotation
      const randomRotation = gsap.utils.random(
        -maxRotationDeg,
        maxRotationDeg,
        1,
      );
      img.dataset.initialRotation = String(randomRotation);
    }
  }
}

if (!customElements.get("find-your-mysa")) {
  customElements.define("find-your-mysa", FindYourMysa);
}
